import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Product } from "../types";
import { useInventory } from "../hooks/useInventory";
import { AlertTriangle } from "lucide-react";
import { AdjustmentReferenceField } from "./AdjustmentReferenceField";

interface BulkActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionType: "stock" | "image" | "delete" | null;
  selectedProductIds: string[];
  products: Product[];
  onSuccess: () => void;
}

const BulkActionModal: React.FC<BulkActionModalProps> = ({
  isOpen,
  onClose,
  actionType,
  selectedProductIds,
  products,
  onSuccess
}) => {
  const [quantity, setQuantity] = useState(1);
  // Optional operator reference / order number — stored on the adjustment-
  // document header (bulk_import_batches.reference).
  const [reference, setReference] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // BLU-1189: gated on `isOpen`. The modal is mounted with the rest of the
  // table chrome and its key carries no production-type filter, so an ungated
  // catalogue here is a second full fetch the moment the operator filters the
  // Inventory page. `bulkAddStock` resolves the selected ids against that
  // catalogue, so Add Stock stays disabled until it has arrived.
  const {
    bulkAddStock,
    bulkUpdateProductImage,
    bulkDeleteProducts,
    loading: catalogueLoading,
  } = useInventory(undefined, { enabled: isOpen });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (selectedProductIds.length === 0) return;

    try {
      setIsSubmitting(true);

      if (actionType === "stock" && quantity > 0) {
        // Partial failure RESOLVES with ok=false (some rows already moved
        // stock and are on the adjustment document — the mutation layer
        // toasts the applied/failed split). Keep the modal + selection so
        // the operator can see what happened instead of blind-retrying the
        // whole set and double-applying the successful rows.
        const ok = await bulkAddStock(selectedProductIds, quantity, reference.trim() || undefined);
        if (!ok) return;
        setReference("");
        onSuccess();
      } else if (actionType === "image" && imageFile) {
        await bulkUpdateProductImage(selectedProductIds, imageFile);
        onSuccess();
      } else if (actionType === "delete") {
        setShowDeleteConfirm(true);
        setIsSubmitting(false);
        return;
      }
    } catch (_error) {
      // Error is handled by the mutation's onError callback
      // No need to handle it here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsSubmitting(true);
      await bulkDeleteProducts(selectedProductIds);
      setShowDeleteConfirm(false);
      onSuccess();
    } catch (_error) {
      // Error is handled by the mutation's onError callback
      // No need to handle it here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {actionType === "stock"
                ? "Add Stock to Multiple Products"
                : actionType === "image"
                  ? "Update Image for Multiple Products"
                  : "Delete Multiple Products"}
            </DialogTitle>
            {actionType === "delete" && (
              <DialogDescription className="text-destructive flex items-center gap-2 mt-2">
                <AlertTriangle className="h-4 w-4" />
                This action cannot be undone. Products will be permanently deleted.
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Selected {selectedProductIds.length} product{selectedProductIds.length !== 1 ? 's' : ''}
              </p>
              <ul className="mt-2 text-sm max-h-32 overflow-y-auto border rounded-md p-2">
                {products.map(product => (
                  <li key={product.id} className="py-1">
                    {product.sku}
                    {product.category.type && ` - ${product.category.type}`}
                    {product.category.size && ` (${product.category.size})`}
                  </li>
                ))}
              </ul>
            </div>

            {actionType === "stock" && (
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="quantity">Quantity to Add</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                  />
                </div>
                <AdjustmentReferenceField
                  id="bulk-stock-reference"
                  value={reference}
                  onChange={setReference}
                />
              </div>
            )}

            {actionType === "image" && (
              <div className="grid gap-2">
                <Label htmlFor="image">Upload Image</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                {imageFile && (
                  <div className="mt-2">
                    <p className="text-sm text-muted-foreground">Selected: {imageFile.name}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                (actionType === "stock" && (quantity <= 0 || catalogueLoading)) ||
                (actionType === "image" && !imageFile)
              }
              variant={actionType === "delete" ? "destructive" : "default"}
              className={actionType !== "delete" ? "bg-threadheads-yellow text-black hover:bg-threadheads-yellow/90" : ""}
            >
              {isSubmitting ? "Processing..." :
                actionType === "stock" ? (catalogueLoading ? "Loading products..." : "Add Stock") :
                actionType === "image" ? "Upload Image" :
                "Delete Products"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete {selectedProductIds.length} product{selectedProductIds.length !== 1 ? 's' : ''}
              and all associated data from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteConfirm(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? "Processing..." : "Yes, delete products"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BulkActionModal;
